import { danishCountryParts } from "@/data/countryparts";
import { municipalities } from "@/data/municipalities";
import { regions } from "@/data/regions";
import { usePostalCodes } from "@/services/postal-codes.service";
import { formatRelativeDate } from "@/utils/filter-utils";
import { formatListValues } from "@/utils/sidebar";
import { Map as MapIcon } from "lucide-react";
import { match } from "ts-pattern";
import { getFilterByFilterName, type FilterCategory } from "../config";
import { createFilterGroup, filterGroupTypes } from "../filter-types";

export const geographyFilterCategory = {
  id: "geography",
  title: "Geografi",
  icon: MapIcon,
  description: "Virksomhedernes geografiske placeringer",
  filters: {
    postalCodes: createFilterGroup({
      id: "postal-codes",
      title: "Postnummer",
      keywords: ["område", "by", "adresse"],
      filters: {
        value: {
          fieldName: "BasicInfo.PostalCode",
          filterName: "geography-postal-code",
        },
      },
      filterType: filterGroupTypes.postalCode,
      DisplayValue: ({ filters }) => {
        const { data: postalCodes } = usePostalCodes();

        const postalCodesFilter = getFilterByFilterName(
          "geography-postal-code",
          filters,
        );

        if (!postalCodesFilter) return "Ikke angivet";

        const postalCodeTitles = new Map(
          postalCodes?.map((code) => [String(code.value), code.title]),
        );

        const postalCodesWithTitles =
          postalCodesFilter.values?.map(
            (code) => postalCodeTitles.get(code) || code,
          ) || [];

        return match(postalCodesFilter.operator)
          .with("Between", () => {
            return `Mellem "${postalCodesWithTitles[0]}" og "${postalCodesWithTitles[1]}"`;
          })
          .with("NotEqual", () => {
            const selectedCodes = formatListValues(
              postalCodesWithTitles,
              "eller",
            );
            return `Ikke lig med ${selectedCodes}`;
          })
          .otherwise(() => {
            const selectedCodes = formatListValues(
              postalCodesWithTitles,
              "eller",
            );
            return `Lig med ${selectedCodes}`;
          });
      },
    }),
    countryParts: createFilterGroup({
      id: "countryPart",
      title: "Landsdel",
      keywords: ["område", "adresse", "region"],
      filters: {
        value: {
          fieldName: "countryPart",
          filterName: "geography-countrypart",
        },
      },
      filterType: filterGroupTypes.countryPart,
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const countryPartFilter = getFilterByFilterName(
          "geography-countrypart",
          filters,
        );
        if (!countryPartFilter) return "Ikke angivet";
        const { operator, values } = countryPartFilter;
        const selectedCountryparts = formatListValues(
          values
            ?.map((value) =>
              String(
                danishCountryParts.find((r) => r.value === Number(value))
                  ?.title,
              ),
            )
            .filter(Boolean) ?? [],
          "eller",
          !ignoreOperator,
        );

        if (ignoreOperator) {
          return selectedCountryparts;
        }

        return operator === "Equal"
          ? `Lig med ${selectedCountryparts}`
          : `Ikke lig med ${selectedCountryparts}`;
      },
    }),
    regions: createFilterGroup({
      id: "regions",
      title: "Region",
      keywords: ["område", "adresse", "landsdel"],
      filters: {
        value: { fieldName: "region", filterName: "geography-region" },
      },
      filterType: filterGroupTypes.region,
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const regionsFilter = getFilterByFilterName(
          "geography-region",
          filters,
        );
        if (!regionsFilter) return "Ikke angivet";
        const { operator, values } = regionsFilter;
        const selectedRegions = formatListValues(
          values
            ?.map((value) =>
              String(regions.find((r) => r.value === Number(value))?.title),
            )
            .filter(Boolean) ?? [],
          "eller",
          !ignoreOperator,
        );

        if (ignoreOperator) {
          return selectedRegions;
        }

        return operator === "Equal"
          ? `Lig med ${selectedRegions}`
          : `Ikke lig med ${selectedRegions}`;
      },
    }),
    municipalities: createFilterGroup({
      id: "municipalities",
      title: "Kommune",
      keywords: ["område", "adresse"],
      filters: {
        value: {
          fieldName: "municipalityCode",
          filterName: "geography-municipality",
        },
      },
      filterType: filterGroupTypes.municipality,
      DisplayValue: ({ filters, ignoreOperator = false }) => {
        const municipalitiesFilter = getFilterByFilterName(
          "geography-municipality",
          filters,
        );
        if (!municipalitiesFilter) return "Ikke angivet";
        const { operator, values } = municipalitiesFilter;
        const selectedMunicipalities = formatListValues(
          values?.map(
            (value) =>
              municipalities.find((m) => m.value === Number(value))!.title,
          ) ?? [],
          "eller",
          !ignoreOperator,
        );

        if (ignoreOperator) {
          return selectedMunicipalities;
        }

        return operator === "Equal"
          ? `Lig med ${selectedMunicipalities}`
          : `Ikke lig med ${selectedMunicipalities}`;
      },
    }),
    AddressChangedEvent: createFilterGroup({
      id: "address-change",
      title: "Adresseændring",
      keywords: ["flytning"],
      eventType: "AddressChanged",
      description:
        "Indikerer om virksomheden har ændret adresse, hvilket kan være tegn på udvikling.",
      filters: {
        value: {
          fieldName: "AddressChangedEvent",
          filterName: "geography-address-change",
        },
      },
      filterType: filterGroupTypes.event,
      DisplayValue: ({ filters }) => {
        const addressChangeFilter = getFilterByFilterName(
          "geography-address-change",
          filters,
        );

        if (addressChangeFilter?.operator !== "EventFilter")
          return "Ikke angivet";

        const { timeOperator: operator, timeValues: values } =
          addressChangeFilter;

        if (!values || !operator) return "Ikke angivet";

        return formatRelativeDate(values, operator);
      },
    }),
  },
} satisfies FilterCategory;
